import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  AsnListItem,
  AsnCanonicalResponse,
  PackMasterRow,
  CartonizationResponse,
  CartonizationMultiShipmentResponse,
  CartonizationShipmentPlan,
  CartonizationLineInput,
  AsnIngestBatchResponse,
} from '../../services/api/packAnalysisService';
import type { Item } from '../../services/api/itemMasterService';
import {
  type EditorPoLine,
  type ResolvedItemRef,
  type PrepackGroupMeta,
  type PrepackCompositionSubItem,
  makeEmptyManualLine,
  makeResolvedSkuLine,
  makePrepackChildLine,
  nextEditorLineId,
  toEditorLine,
} from '../../features/pack-analysis-workbench/components/Simulator/lineModel';
import {
  ingestBatch,
  cancelIngestBatch,
  fetchAsnList,
  fetchAsnCanonical,
  fetchPackMasterList,
  fetchPackMasterById,
  cartonizeSimulate,
  cartonizePackPlannerProgressive,
  cartonizePackPlannerAlternatives,
  searchItemsForSimulator,
  searchItemsForPackPlanner,
  INGEST_CANCELLED_SENTINEL,
} from '../thunks/packAnalysisWorkbenchThunks';

export interface IngestBatchHistoryEntry {
  startedAt: number;       // epoch ms
  totalElapsedMs: number;  // wall clock from submit → response
  result: AsnIngestBatchResponse;
}

export type PerFileStatus = 'queued' | 'processing' | 'done' | 'failed' | 'cancelled';

// Server-side status code for files skipped by a batch cancel.
// Mirrors Doc856_Ingest_Response.StatusCodes.Cancelled (-2005).
const STATUS_CODE_CANCELLED = -2005;

export interface PerFileProgress {
  fileIndex: number;
  fileName: string;
  status: PerFileStatus;
  startedAt?: number;
  completedAt?: number;
  doc856Id?: number | null;
  statusCode?: number | null;
  statusText?: string | null;
  elapsedMs?: number | null;
}

/**
 * Spec: pack-analysis-ingest-background-progress Req 5.1.
 * Summary record surfaced by the AppShell ingest-progress badge + popover so
 * progress visibility survives navigation away from the IngestTab. Stamped
 * on thunk entry; cleared when the SSE emits batch_completed (via the thunk's
 * finally block — single transition fires the boot hook's effect).
 */
export interface ActiveBatchSummary {
  clientBatchId: string;
  startedAt: string;            // ISO 8601; drives elapsed-time tick + ETA
  totalFiles: number;           // patched on first batch_started SSE event
  completedFiles: number;
  failedFiles: number;
  currentFileName: string | null;
  lastEventAt: string;          // ISO; for stale-stream detection (future)
}

// Spec: pack-planner — extracted from the existing `simulator` sub-slice
// so the `packPlanner` field can mirror its shape exactly (Req 1.4: each tab
// owns its own form state).
export interface SimulatorTabSubState {
  // PO header the operator is "opening"
  poHeader: {
    buyerPartyCode: string;
    supplierPartyCode: string;
    purchaseOrderNumber: string;
    effectiveAt: string | null; // ISO; null = "now" (BE default)
  };
  // Optional advanced criteria (progressive disclosure — collapsed by default)
  criteria: {
    minCartonFillPct: number | null;
    // Spec pa-cartongroupkey-engine-support — renamed from allowMixPack (gates mixing
    // of different ITEM KEYS); allowMixedCartonGroups is the orthogonal carton-group
    // axis (null ⇒ profile default true / permissive = byte-identical).
    allowMixedItemKeys: boolean | null;
    allowMixedCartonGroups: boolean | null;
    preferredCartonTypeId: number | null;
    channelCode: string | null;
    prepackBuyerCascade: boolean | null;
    bulkBuyerCascade: boolean | null;
  };
  // Editable PO lines — `EditorPoLine` = the wire DTO + `_id`/`_origin`/etc.
  poLines: EditorPoLine[];
  // Expanded prepack groups, keyed by `_prepackGroupId`.
  prepackGroups: Record<string, PrepackGroupMeta>;
  // Per-line item-identifier combobox typeahead.
  itemSearch: {
    q: string;
    items: Item[];
    loading: boolean;
    error: string | null;
  };
  itemMasterPickerOpen: boolean;
  pickerTargetLineId: string | null;
  lastResult: CartonizationResponse | null;
  isSimulating: boolean;
  error: string | null;
  selectedPackMaster: PackMasterRow | null;
  pickerOpen: boolean;
  pickerItems: PackMasterRow[];
  pickerTotalCount: number;
  isPickerLoading: boolean;
  pickerError: string | null;

  // ── Spec pack-planner-build-timeline Phase A.4.2 — dual-mode fetch ──
  //
  // Only populated by the `packPlanner` sub-slice (Simulator stays
  // single-mode). Pack Planner fires two BE requests in parallel per Run:
  //   progressive (includeAlternatives: false) → Plan card + §0 + §2
  //   alternatives (includeAlternatives: true) → §3 PlansConsidered only
  // Per Req 11 — independent loading / error state per request so a failure
  // on one doesn't blank the other's UI. The Plan card prefers
  // `progressiveResult`; falls back to `alternativesResult` per Req 11.6
  // when progressive fails but alternatives succeeds.
  progressiveResult: CartonizationResponse | null;
  alternativesResult: CartonizationResponse | null;
  isLoadingProgressive: boolean;
  isLoadingAlternatives: boolean;
  progressiveError: string | null;
  alternativesError: string | null;
  // Spec pa-shipment-aware-pack-planning — when a request fans out to >1 shipment, the BE returns
  // the composite and these hold the per-shipment plans (progressive + alternatives) and the
  // currently-selected shipment. `progressiveResult` / `alternativesResult` above always mirror the
  // SELECTED shipment's plan, so the existing result panel renders unchanged. Null for a
  // single-shipment run (byte-identical to today).
  shipments: CartonizationShipmentPlan[] | null;
  alternativesShipments: CartonizationShipmentPlan[] | null;
  selectedShipmentKey: string | null;
}

// Spec: pack-analysis-workbench-ui §State shape (design.md)
export interface PackAnalysisWorkbenchState {
  // Ingest — in-flight batch + history (last 20)
  ingest: {
    isInFlight: boolean;
    error: string | null;
    // Set true while a cancel is in flight (button disable, "Cancelling…" label).
    isCancelling: boolean;
    // Sticky flag on the most recent batch — the result panel can render a
    // "cancelled" indicator even though the http response itself succeeded.
    wasCancelled: boolean;
    // The thunk-generated correlation id of the in-flight batch. The cancel
    // button reads this so it can target the right batch without the user
    // having to thread the id through component props.
    currentBatchId: string | null;
    history: IngestBatchHistoryEntry[];
    progress: {
      phase: 'idle' | 'uploading' | 'processing';
      bytesLoaded: number;
      bytesTotal: number;
    };
    // Per-file SSE progress (live during in-flight batch, cleared on next submit).
    // Keyed by 1-based fileIndex from the server. Holds N rows once batch_started
    // arrives, each flips queued → processing → done/failed as SSE events stream.
    perFile: Record<number, PerFileProgress>;
    perFileTotal: number;
    // Spec pack-analysis-ingest-background-progress Req 5.1 — summary for the
    // AppShell badge + popover; survives route changes because it lives in
    // Redux. Single-active-batch v1 invariant: setActiveBatchStarted is a
    // no-op when this is non-null.
    activeBatch: ActiveBatchSummary | null;
    // Snapshot of the FINAL activeBatch summary at completion time. Set by the
    // SSE `batch_completed` reducer (and the thunk's finally safety-net) just
    // before clearing activeBatch. Read by the AppShell boot hook to fire the
    // completion toast / Browser Notification with authoritative counts.
    //
    // Why a separate field (vs `prevRef` in the boot hook): React 18 auto-
    // batches setState across SSE dispatches, so the LAST `file_completed`
    // increment can batch with `batch_completed` into one render. The hook's
    // `prevRef` would then hold the pre-last-file count (off-by-one). Reducers
    // run serially in Redux, so by the time batch_completed dispatches, the
    // summary already reflects the final completedFiles / failedFiles counts.
    // Snapshotting INSIDE the reducer is the only way to capture them.
    // Cleared by the boot hook via `acknowledgeBatchCompletion` after firing
    // side effects.
    lastCompletedBatchSnapshot: ActiveBatchSummary | null;
    // Session-scope flag (cleared on full reload). Flips true after the user
    // either grants / denies / dismisses the Notification permission banner.
    notificationPermissionPrompted: boolean;
  };

  // Browser — list + cached drill-down details
  browser: {
    list: AsnListItem[];
    totalCount: number;
    pageIndex: number;
    pageSize: number;
    filters: {
      status?: string;
      // pack-analysis-top-bar-party-scope — buyer/supplier are no longer filter
      // fields; the list scopes on the top-bar active party at fetch time.
      // ISO 8601 UTC. `dateFrom` inclusive, `dateTo` exclusive (half-open per
      // pack-analysis-asn-browser-pagination-v2 Req 3.1/3.6).
      dateFrom?: string | null;
      dateTo?: string | null;
    };
    // Spec pack-analysis-asn-browser-server-sort. Null sortBy → BE default
    // (CreatedAt DESC). Persisted in URL (?sortBy=&sortDir=).
    sortBy: string | null;
    sortDir: 'asc' | 'desc' | null;
    isListLoading: boolean;
    listError: string | null;
    detailById: Record<number, AsnCanonicalResponse>;
    isDetailLoading: boolean;
    detailError: string | null;
  };

  // Simulator — "open a PO, predict the pack" (Phase E). Shape extracted into
  // `SimulatorTabSubState` so the parallel `packPlanner` tab mirrors it
  // exactly (Pack Planner spec Req 1.4).
  simulator: SimulatorTabSubState;

  // Pack Planner — Spec `pack-planner`. Same engine + state shape as
  // `simulator`; presentation differs (Plan card · By Carton / By Item ·
  // Plan-level + per-row Why panels). Tabs hold independent form state per
  // Pack Planner Req 1.4 — switching tabs preserves each tab's inputs.
  packPlanner: SimulatorTabSubState;
}

// Initial state for one simulator-style tab. Factory function so both
// `simulator` and `packPlanner` get independent objects (deep-clone semantics
// without resorting to structuredClone at module init).
function makeEmptySimulatorTabState(): SimulatorTabSubState {
  return {
    poHeader: { buyerPartyCode: '', supplierPartyCode: '', purchaseOrderNumber: '', effectiveAt: null },
    criteria: { minCartonFillPct: null, allowMixedItemKeys: null, allowMixedCartonGroups: null, preferredCartonTypeId: null, channelCode: null, prepackBuyerCascade: null, bulkBuyerCascade: null },
    poLines: [],
    prepackGroups: {},
    itemSearch: { q: '', items: [], loading: false, error: null },
    itemMasterPickerOpen: false,
    pickerTargetLineId: null,
    lastResult: null,
    isSimulating: false,
    error: null,
    selectedPackMaster: null,
    pickerOpen: false,
    pickerItems: [],
    pickerTotalCount: 0,
    isPickerLoading: false,
    pickerError: null,
    progressiveResult: null,
    alternativesResult: null,
    isLoadingProgressive: false,
    isLoadingAlternatives: false,
    progressiveError: null,
    alternativesError: null,
    shipments: null,
    alternativesShipments: null,
    selectedShipmentKey: null,
  };
}

/**
 * Spec pa-shipment-aware-pack-planning — normalize a cartonize() result (either a single
 * `CartonizationResponse` or the multi-shipment composite) into the slice's dual fields: the plan
 * to DISPLAY now (the first shipment for a composite) + the per-shipment list (null when the run
 * was single-shipment). Keeps the existing result panel reading `progressiveResult` unchanged.
 */
function normalizeCartonizeResult(
  payload: CartonizationResponse | CartonizationMultiShipmentResponse,
): {
  displayPlan: CartonizationResponse | null;
  shipments: CartonizationShipmentPlan[] | null;
  selectedKey: string | null;
} {
  if (payload && 'shipments' in payload && Array.isArray(payload.shipments)) {
    const first = payload.shipments[0] ?? null;
    return {
      displayPlan: first?.plan ?? null,
      shipments: payload.shipments,
      selectedKey: first?.shipmentKey ?? null,
    };
  }
  return { displayPlan: payload as CartonizationResponse, shipments: null, selectedKey: null };
}

/** A fresh, empty PO line for the editor (E.3 add-line). Now an `EditorPoLine`. */
export const makeEmptyPoLine = makeEmptyManualLine;

const initialState: PackAnalysisWorkbenchState = {
  ingest: {
    isInFlight: false,
    error: null,
    isCancelling: false,
    wasCancelled: false,
    currentBatchId: null,
    history: [],
    progress: { phase: 'idle', bytesLoaded: 0, bytesTotal: 0 },
    perFile: {},
    perFileTotal: 0,
    activeBatch: null,
    lastCompletedBatchSnapshot: null,
    notificationPermissionPrompted: false,
  },
  browser: {
    list: [],
    totalCount: 0,
    pageIndex: 1,
    pageSize: 50,
    filters: {},
    sortBy: null,
    sortDir: null,
    isListLoading: false,
    listError: null,
    detailById: {},
    isDetailLoading: false,
    detailError: null,
  },
  simulator: makeEmptySimulatorTabState(),
  // Pack Planner spec: same shape, independent state from simulator.
  packPlanner: makeEmptySimulatorTabState(),
};

const packAnalysisWorkbenchSlice = createSlice({
  name: 'packAnalysisWorkbench',
  initialState,
  reducers: {
    setBrowserFilters(
      state,
      action: PayloadAction<{
        status?: string;
        dateFrom?: string | null;
        dateTo?: string | null;
      }>
    ) {
      state.browser.filters = action.payload;
      state.browser.pageIndex = 1;
    },
    /**
     * Pagination-v2 Req 3.1 / 4.x — bulk-set the entire browser view (filters
     * + pagination + page size). The URL-sync hook calls this on mount with
     * the parsed URL params so a single render captures the whole state.
     * Unspecified fields fall back to the existing slice value (no clearing).
     */
    setBrowserState(
      state,
      action: PayloadAction<{
        status?: string;
        dateFrom?: string | null;
        dateTo?: string | null;
        pageIndex?: number;
        pageSize?: number;
        sortBy?: string | null;
        sortDir?: 'asc' | 'desc' | null;
      }>
    ) {
      const p = action.payload;
      state.browser.filters = {
        status: p.status,
        dateFrom: p.dateFrom,
        dateTo: p.dateTo,
      };
      if (p.pageIndex != null) state.browser.pageIndex = p.pageIndex;
      if (p.pageSize != null) state.browser.pageSize = p.pageSize;
      if (p.sortBy !== undefined) state.browser.sortBy = p.sortBy;
      if (p.sortDir !== undefined) state.browser.sortDir = p.sortDir;
    },
    /**
     * Spec pack-analysis-asn-browser-server-sort — set/toggle the active sort
     * column. Resets pageIndex to 1 (sorting from a non-first page would be
     * disorienting). Pass {sortBy: null, sortDir: null} to clear the sort.
     */
    setBrowserSort(
      state,
      action: PayloadAction<{ sortBy: string | null; sortDir: 'asc' | 'desc' | null }>
    ) {
      state.browser.sortBy = action.payload.sortBy;
      state.browser.sortDir = action.payload.sortDir;
      state.browser.pageIndex = 1;
    },
    clearBrowserDetailCache(state) {
      state.browser.detailById = {};
    },
    /**
     * Drop a single ASN's cached canonical detail so the next drill-down
     * re-fetches it. Used after a Reprocess (Phase D.6) — the canonical layer
     * may have changed, so the stale snapshot must not be served.
     */
    invalidateAsnDetail(state, action: PayloadAction<number>) {
      delete state.browser.detailById[action.payload];
    },
    setSimulatorSelected(state, action: PayloadAction<PackMasterRow | null>) {
      state.simulator.selectedPackMaster = action.payload;
    },
    clearSimulatorResult(state) {
      state.simulator.lastResult = null;
      state.simulator.error = null;
    },

    // ── Simulator: PO header + criteria + lines (Phase E) ──────────────────
    setPoHeader(
      state,
      action: PayloadAction<Partial<PackAnalysisWorkbenchState['simulator']['poHeader']>>
    ) {
      Object.assign(state.simulator.poHeader, action.payload);
    },
    setCriteria(
      state,
      action: PayloadAction<Partial<PackAnalysisWorkbenchState['simulator']['criteria']>>
    ) {
      Object.assign(state.simulator.criteria, action.payload);
    },
    addPoLine(state, action: PayloadAction<EditorPoLine | CartonizationLineInput | undefined>) {
      state.simulator.poLines.push(action.payload ? toEditorLine(action.payload) : makeEmptyManualLine());
    },
    updatePoLine(state, action: PayloadAction<{ index: number; patch: Partial<EditorPoLine> }>) {
      const row = state.simulator.poLines[action.payload.index];
      if (row) Object.assign(row, action.payload.patch);
    },
    /** Patch a line by its stable `_id` — used by the item-identifier combobox
     *  (the row index can shift under an async resolve). */
    updatePoLineById(state, action: PayloadAction<{ id: string; patch: Partial<EditorPoLine> }>) {
      const row = state.simulator.poLines.find((l) => l._id === action.payload.id);
      if (row) Object.assign(row, action.payload.patch);
    },
    removePoLine(state, action: PayloadAction<number>) {
      state.simulator.poLines.splice(action.payload, 1);
    },
    /** Bulk-replace the line list — used by "Use sample data" + pattern seeding.
     *  Plain `CartonizationLineInput[]` (sample/seed) is wrapped into manual `EditorPoLine`s;
     *  any prepack-group metadata is dropped (its children, if any, are gone too). */
    setPoLines(state, action: PayloadAction<Array<EditorPoLine | CartonizationLineInput>>) {
      state.simulator.poLines = action.payload.map(toEditorLine);
      state.simulator.prepackGroups = {};
    },
    // ── Simulator: item-aware line entry (spec …item-aware-line-entry, Phase A) ──
    /** Append a line populated from a picked item-master SKU row. */
    addResolvedLine(state, action: PayloadAction<ResolvedItemRef>) {
      state.simulator.poLines.push(makeResolvedSkuLine(action.payload));
    },
    // ── Simulator: prepack groups (Phase C) ──
    /** Add an expanded prepack group: a `PrepackGroupMeta` keyed by a new groupId
     *  + one read-only `prepackChild` line per component sub-item. When
     *  `replaceLineId` is given (the line whose combobox / picker selected the
     *  PACK item — typically still holding the typed prepack code), that line is
     *  removed and the child lines are spliced in at its slot, so the group
     *  visually replaces it (no stray "manual key — not resolved" line left
     *  behind). When omitted, the children are appended at the end. */
    addPrepackGroup(
      state,
      action: PayloadAction<{ prepackCode: string; packItemId: number; description?: string | null; subItems: PrepackCompositionSubItem[]; replaceLineId?: string }>
    ) {
      const { prepackCode, packItemId, description, subItems, replaceLineId } = action.payload;
      const groupId = nextEditorLineId('pg');
      const unitsPerPrepack = subItems.reduce((s, x) => s + (Number(x.quantity) || 0), 0);
      state.simulator.prepackGroups[groupId] = {
        prepackCode,
        packItemId,
        description: description ?? null,
        prepackCount: 1,
        unitsPerPrepack,
      };
      const children = subItems.map((sub) => makePrepackChildLine(sub, groupId, prepackCode, 1));
      const replaceIdx = replaceLineId
        ? state.simulator.poLines.findIndex((l) => l._id === replaceLineId)
        : -1;
      if (replaceIdx >= 0) {
        // Don't consume another group's child as the "host" line — only a plain
        // (non-prepackChild) line is a legitimate replacement target.
        const hostIsChild = state.simulator.poLines[replaceIdx]._origin === 'prepackChild';
        if (hostIsChild) state.simulator.poLines.push(...children);
        else state.simulator.poLines.splice(replaceIdx, 1, ...children);
      } else {
        state.simulator.poLines.push(...children);
      }
    },
    /** Remove a whole prepack group — drops the meta and every child line. */
    removePrepackGroup(state, action: PayloadAction<string>) {
      delete state.simulator.prepackGroups[action.payload];
      state.simulator.poLines = state.simulator.poLines.filter((l) => l._prepackGroupId !== action.payload);
    },
    /** Set a group's prepack count `N` (≥ 1) — rewrites every child's `orderQuantity`
     *  to `_perPrepackQty × N`. */
    setPrepackCount(state, action: PayloadAction<{ groupId: string; count: number }>) {
      const g = state.simulator.prepackGroups[action.payload.groupId];
      if (!g) return;
      const n = Math.max(1, Math.floor(Number(action.payload.count)) || 1);
      g.prepackCount = n;
      for (const l of state.simulator.poLines) {
        if (l._prepackGroupId === action.payload.groupId) l.orderQuantity = (l._perPrepackQty ?? 0) * n;
      }
    },
    /** Stamp a resolved item onto the line with this `_id` — `itemKey` becomes the
     *  resolved canonical key; the row chip switches to "resolved". */
    setLineResolution(state, action: PayloadAction<{ id: string; ref: ResolvedItemRef }>) {
      const row = state.simulator.poLines.find((l) => l._id === action.payload.id);
      if (row) {
        row._origin = 'sku';
        row._resolvedItem = action.payload.ref;
        row.itemKey = action.payload.ref.resolvedCanonicalKey;
      }
    },
    /** Revert a resolved line to "manual" (keeps the typed `itemKey` so the
     *  operator can edit it). Triggered when they change a resolved row's key. */
    clearLineResolution(state, action: PayloadAction<string>) {
      const row = state.simulator.poLines.find((l) => l._id === action.payload);
      if (row) {
        row._origin = 'manual';
        delete row._resolvedItem;
      }
    },
    /** Item-identifier combobox: typed query (debounced fetch lives in the thunk). */
    setItemSearchQuery(state, action: PayloadAction<string>) {
      state.simulator.itemSearch.q = action.payload;
    },
    clearItemSearch(state) {
      state.simulator.itemSearch = { q: '', items: [], loading: false, error: null };
    },
    /** Open/close the Item Master Picker dialog; `targetLineId` = the line being
     *  filled (omit / null = "append a new line"). */
    setItemMasterPickerOpen(state, action: PayloadAction<{ open: boolean; targetLineId?: string | null }>) {
      state.simulator.itemMasterPickerOpen = action.payload.open;
      state.simulator.pickerTargetLineId = action.payload.open ? action.payload.targetLineId ?? null : null;
    },
    setPickerOpen(state, action: PayloadAction<boolean>) {
      state.simulator.pickerOpen = action.payload;
    },

    // ── Pack Planner: parallel reducers to the simulator's (spec pack-planner Req 1.4) ──
    // Same shape, same semantics, independent state field. Phase 1.A scope:
    // form-state reducers + picker-open toggles. Item search + prepack-count
    // helpers land in later Phase 1 sub-phases when the result-panel + line-
    // editor wiring actually consumes them.
    clearPackPlannerResult(state) {
      state.packPlanner.lastResult = null;
      state.packPlanner.error = null;
      // Phase A.4.2 — dual-mode fetch fields also need clearing.
      state.packPlanner.progressiveResult = null;
      state.packPlanner.alternativesResult = null;
      state.packPlanner.progressiveError = null;
      state.packPlanner.alternativesError = null;
      // Spec pa-shipment-aware-pack-planning — clear multi-shipment state too.
      state.packPlanner.shipments = null;
      state.packPlanner.alternativesShipments = null;
      state.packPlanner.selectedShipmentKey = null;
    },
    /**
     * Spec pa-shipment-aware-pack-planning — pick which shipment's plan the result panel shows.
     * Re-points `progressiveResult` / `alternativesResult` to the selected shipment's plan so the
     * existing panel renders it unchanged. No-op when the run was single-shipment.
     */
    selectPackPlannerShipment(state, action: PayloadAction<string | null>) {
      const key = action.payload;
      state.packPlanner.selectedShipmentKey = key;
      const match = (s: CartonizationShipmentPlan) => (s.shipmentKey ?? null) === key;
      const prog = state.packPlanner.shipments?.find(match)?.plan ?? null;
      if (prog) {
        state.packPlanner.progressiveResult = prog;
        state.packPlanner.lastResult = prog;
      }
      const alt = state.packPlanner.alternativesShipments?.find(match)?.plan ?? null;
      if (alt) {
        state.packPlanner.alternativesResult = alt;
      }
    },
    setPackPlannerPoHeader(
      state,
      action: PayloadAction<Partial<SimulatorTabSubState['poHeader']>>
    ) {
      Object.assign(state.packPlanner.poHeader, action.payload);
    },
    setPackPlannerCriteria(
      state,
      action: PayloadAction<Partial<SimulatorTabSubState['criteria']>>
    ) {
      Object.assign(state.packPlanner.criteria, action.payload);
    },
    addPackPlannerPoLine(state, action: PayloadAction<EditorPoLine | CartonizationLineInput | undefined>) {
      state.packPlanner.poLines.push(action.payload ? toEditorLine(action.payload) : makeEmptyManualLine());
    },
    updatePackPlannerPoLine(state, action: PayloadAction<{ index: number; patch: Partial<EditorPoLine> }>) {
      const row = state.packPlanner.poLines[action.payload.index];
      if (row) Object.assign(row, action.payload.patch);
    },
    updatePackPlannerPoLineById(state, action: PayloadAction<{ id: string; patch: Partial<EditorPoLine> }>) {
      const row = state.packPlanner.poLines.find((l) => l._id === action.payload.id);
      if (row) Object.assign(row, action.payload.patch);
    },
    removePackPlannerPoLine(state, action: PayloadAction<number>) {
      state.packPlanner.poLines.splice(action.payload, 1);
    },
    setPackPlannerPoLines(state, action: PayloadAction<Array<EditorPoLine | CartonizationLineInput>>) {
      state.packPlanner.poLines = action.payload.map(toEditorLine);
      state.packPlanner.prepackGroups = {};
    },
    addPackPlannerPrepackGroup(
      state,
      action: PayloadAction<{ prepackCode: string; packItemId: number; description?: string | null; subItems: PrepackCompositionSubItem[]; replaceLineId?: string }>
    ) {
      const { prepackCode, packItemId, description, subItems, replaceLineId } = action.payload;
      const groupId = nextEditorLineId('pg');
      const unitsPerPrepack = subItems.reduce((s, x) => s + (Number(x.quantity) || 0), 0);
      state.packPlanner.prepackGroups[groupId] = {
        prepackCode,
        packItemId,
        description: description ?? null,
        prepackCount: 1,
        unitsPerPrepack,
      };
      const children = subItems.map((sub) => makePrepackChildLine(sub, groupId, prepackCode, 1));
      const replaceIdx = replaceLineId
        ? state.packPlanner.poLines.findIndex((l) => l._id === replaceLineId)
        : -1;
      if (replaceIdx >= 0) {
        const hostIsChild = state.packPlanner.poLines[replaceIdx]._origin === 'prepackChild';
        if (hostIsChild) state.packPlanner.poLines.push(...children);
        else state.packPlanner.poLines.splice(replaceIdx, 1, ...children);
      } else {
        state.packPlanner.poLines.push(...children);
      }
    },
    setPackPlannerItemMasterPickerOpen(state, action: PayloadAction<{ open: boolean; targetLineId?: string | null }>) {
      state.packPlanner.itemMasterPickerOpen = action.payload.open;
      state.packPlanner.pickerTargetLineId = action.payload.open ? action.payload.targetLineId ?? null : null;
    },
    setPackPlannerPickerOpen(state, action: PayloadAction<boolean>) {
      state.packPlanner.pickerOpen = action.payload;
    },
    addPackPlannerResolvedLine(state, action: PayloadAction<ResolvedItemRef>) {
      state.packPlanner.poLines.push(makeResolvedSkuLine(action.payload));
    },
    setPackPlannerLineResolution(state, action: PayloadAction<{ id: string; ref: ResolvedItemRef }>) {
      const row = state.packPlanner.poLines.find((l) => l._id === action.payload.id);
      if (row) {
        row._origin = 'sku';
        row._resolvedItem = action.payload.ref;
        row.itemKey = action.payload.ref.resolvedCanonicalKey;
      }
    },
    clearPackPlannerLineResolution(state, action: PayloadAction<string>) {
      const row = state.packPlanner.poLines.find((l) => l._id === action.payload);
      if (row) {
        row._origin = 'manual';
        delete row._resolvedItem;
      }
    },
    setPackPlannerItemSearchQuery(state, action: PayloadAction<string>) {
      state.packPlanner.itemSearch.q = action.payload;
    },
    clearPackPlannerItemSearch(state) {
      state.packPlanner.itemSearch = { q: '', items: [], loading: false, error: null };
    },
    setPackPlannerPrepackCount(state, action: PayloadAction<{ groupId: string; count: number }>) {
      const g = state.packPlanner.prepackGroups[action.payload.groupId];
      if (!g) return;
      const n = Math.max(1, Math.floor(Number(action.payload.count)) || 1);
      g.prepackCount = n;
      for (const l of state.packPlanner.poLines) {
        if (l._prepackGroupId === action.payload.groupId) l.orderQuantity = (l._perPrepackQty ?? 0) * n;
      }
    },
    removePackPlannerPrepackGroup(state, action: PayloadAction<string>) {
      delete state.packPlanner.prepackGroups[action.payload];
      state.packPlanner.poLines = state.packPlanner.poLines.filter((l) => l._prepackGroupId !== action.payload);
    },

    /**
     * Tenant switch — clear all in-memory workbench state per spec Req 6.3.
     * Caller (TenantLoader) dispatches this when activeTenantId changes.
     */
    resetOnTenantSwitch() {
      return initialState;
    },
    clearIngestHistory(state) {
      state.ingest.history = [];
      state.ingest.error = null;
    },
    setIngestProgress(
      state,
      action: PayloadAction<{
        phase: 'idle' | 'uploading' | 'processing';
        bytesLoaded: number;
        bytesTotal: number;
      }>
    ) {
      state.ingest.progress = action.payload;
    },
    setIngestCurrentBatchId(state, action: PayloadAction<string | null>) {
      state.ingest.currentBatchId = action.payload;
    },
    /**
     * Spec pack-analysis-ingest-background-progress Req 5.2. Stamped on
     * `ingestBatch` thunk entry. Single-active-batch v1 guard: no-op when an
     * activeBatch already exists (with a console.warn for dev visibility) so
     * a second start during an in-flight batch can't silently overwrite the
     * badge target.
     */
    setActiveBatchStarted(
      state,
      action: PayloadAction<{ clientBatchId: string; totalFiles: number; startedAt: string }>
    ) {
      if (state.ingest.activeBatch != null) {
        if (typeof console !== 'undefined') {
          console.warn(
            'setActiveBatchStarted called while activeBatch is non-null; ignoring (single-active-batch v1 invariant).'
          );
        }
        return;
      }
      state.ingest.activeBatch = {
        clientBatchId: action.payload.clientBatchId,
        startedAt: action.payload.startedAt,
        totalFiles: action.payload.totalFiles,
        completedFiles: 0,
        failedFiles: 0,
        currentFileName: null,
        lastEventAt: action.payload.startedAt,
      };
    },
    /**
     * Spec pack-analysis-ingest-background-progress Req 5.3. Cleared by the
     * thunk's finally block (or terminal-error path) AND by the SSE
     * batch_completed branch as a unified completion edge. Snapshots the
     * final activeBatch summary into `lastCompletedBatchSnapshot` before
     * clearing so the AppShell boot hook can fire toast + Browser
     * Notification with authoritative counts (the React-18 auto-batching trap
     * — see `lastCompletedBatchSnapshot` field doc on the state shape for
     * why this snapshot lives in the slice rather than via React's render
     * cycle). Idempotent — a second call when activeBatch is already null is
     * a no-op (no snapshot is overwritten).
     */
    setActiveBatchCleared(state) {
      if (state.ingest.activeBatch != null) {
        state.ingest.lastCompletedBatchSnapshot = { ...state.ingest.activeBatch };
        state.ingest.activeBatch = null;
      }
    },
    /**
     * Spec pack-analysis-ingest-background-progress — boot hook signals it
     * has consumed the snapshot (toast + Browser Notification fired). Clears
     * the snapshot so the same completion can't fire twice on a re-render.
     */
    acknowledgeBatchCompletion(state) {
      state.ingest.lastCompletedBatchSnapshot = null;
    },
    /**
     * Spec pack-analysis-ingest-background-progress Req 4.1 / 4.6. Flipped
     * when the user grants / denies / dismisses the Notification permission
     * banner. Suppresses re-prompting for the rest of the session.
     */
    setNotificationPermissionPrompted(state) {
      state.ingest.notificationPermissionPrompted = true;
    },
    /**
     * Apply one SSE progress event from the live ingest stream. The server emits
     * (in order): batch_started → file_started/file_completed * N (interleaved
     * under parallelism) → batch_completed. We treat batch_started as "seed all
     * N rows queued" so the UI can render the placeholder table immediately.
     */
    applyIngestProgressEvent(
      state,
      action: PayloadAction<{
        eventType: 'batch_started' | 'file_started' | 'file_completed' | 'batch_completed';
        totalFiles: number;
        fileIndex: number;
        fileName?: string | null;
        doc856Id?: number | null;
        statusCode?: number | null;
        statusText?: string | null;
        elapsedMs?: number | null;
      }>
    ) {
      const evt = action.payload;
      // Spec pack-analysis-ingest-background-progress Req 5.1 — update the
      // activeBatch summary in parallel with the per-file array. Lives below
      // the per-event branches to keep the existing state.ingest.perFile logic
      // byte-equal.
      const summary = state.ingest.activeBatch;
      const nowIso = new Date().toISOString();
      switch (evt.eventType) {
        case 'batch_started':
          state.ingest.perFile = {};
          state.ingest.perFileTotal = evt.totalFiles;
          if (summary != null) {
            summary.totalFiles = evt.totalFiles;
            summary.currentFileName = null;
            summary.lastEventAt = nowIso;
          }
          break;
        case 'file_started': {
          const existing = state.ingest.perFile[evt.fileIndex];
          state.ingest.perFile[evt.fileIndex] = {
            ...existing,
            fileIndex: evt.fileIndex,
            fileName: evt.fileName ?? existing?.fileName ?? '',
            status: 'processing',
            startedAt: Date.now(),
          };
          if (summary != null) {
            summary.currentFileName = evt.fileName ?? existing?.fileName ?? null;
            summary.lastEventAt = nowIso;
          }
          break;
        }
        case 'file_completed': {
          const existing = state.ingest.perFile[evt.fileIndex];
          // statusCode === 0 == success per backend Result<T> convention.
          // statusCode === -2005 == file was queued when the batch was cancelled.
          const succeeded = evt.statusCode === 0;
          const cancelled = evt.statusCode === STATUS_CODE_CANCELLED;
          const terminalStatus: PerFileStatus = succeeded
            ? 'done'
            : cancelled
              ? 'cancelled'
              : 'failed';
          state.ingest.perFile[evt.fileIndex] = {
            ...existing,
            fileIndex: evt.fileIndex,
            fileName: evt.fileName ?? existing?.fileName ?? '',
            status: terminalStatus,
            completedAt: Date.now(),
            doc856Id: evt.doc856Id,
            statusCode: evt.statusCode,
            statusText: evt.statusText,
            elapsedMs: evt.elapsedMs,
          };
          if (summary != null) {
            summary.completedFiles += 1;
            // Cancelled files don't count as failures — operator-driven, not
            // an ingest defect (matches the IngestTab results-table coloring).
            if (!succeeded && !cancelled) summary.failedFiles += 1;
            summary.lastEventAt = nowIso;
          }
          break;
        }
        case 'batch_completed':
          // Spec pack-analysis-ingest-background-progress — primary clear path.
          // The BE (`AsnIngestBatch_Command` line ~208) publishes batch_completed
          // BEFORE returning the HTTP response, so this is the earliest reliable
          // edge for "BE is fully done with this batch".
          //
          // Snapshot-then-clear semantics: the FINAL summary is captured into
          // `lastCompletedBatchSnapshot` so the AppShell boot hook fires toast
          // / Browser Notification with authoritative counts. Reducers run
          // serially in Redux — by the time this branch executes, the
          // preceding `file_completed` for the LAST file has already
          // incremented `summary.completedFiles`, so the snapshot reflects
          // the true final state. (Cannot rely on React render-time refs in
          // the boot hook because React 18 auto-batches the last
          // file_completed + batch_completed dispatches into one render —
          // see the field doc on `lastCompletedBatchSnapshot`.)
          //
          // The thunk's finally STILL dispatches setActiveBatchCleared as a
          // safety-net — idempotent (re-snapshotting null is a no-op),
          // guards against SSE connection drops mid-stream.
          if (summary != null) {
            summary.lastEventAt = nowIso;
            state.ingest.lastCompletedBatchSnapshot = { ...summary };
          }
          state.ingest.activeBatch = null;
          break;
      }
    },
  },
  extraReducers: (builder) => {
    // ── Ingest batch ──
    builder.addCase(ingestBatch.pending, (state) => {
      state.ingest.isInFlight = true;
      state.ingest.error = null;
      state.ingest.isCancelling = false;
      state.ingest.wasCancelled = false;
      state.ingest.progress = { phase: 'uploading', bytesLoaded: 0, bytesTotal: 0 };
      // Clear any previous batch's per-file rows so the live table doesn't briefly
      // mix old + new rows during the upload phase.
      state.ingest.perFile = {};
      state.ingest.perFileTotal = 0;
    });
    builder.addCase(ingestBatch.fulfilled, (state, action) => {
      state.ingest.isInFlight = false;
      state.ingest.isCancelling = false;
      state.ingest.currentBatchId = null;
      state.ingest.progress = { phase: 'idle', bytesLoaded: 0, bytesTotal: 0 };
      state.ingest.history.unshift({
        startedAt: Date.now() - action.payload.totalElapsedMs,
        totalElapsedMs: action.payload.totalElapsedMs,
        result: action.payload.data,
      });
      if (state.ingest.history.length > 20) state.ingest.history.length = 20;
    });
    builder.addCase(ingestBatch.rejected, (state, action) => {
      state.ingest.isInFlight = false;
      state.ingest.isCancelling = false;
      state.ingest.currentBatchId = null;
      state.ingest.progress = { phase: 'idle', bytesLoaded: 0, bytesTotal: 0 };
      // The cancel sentinel is a normal user-driven outcome, not an error.
      // Don't surface it in the red error alert — the per-file table + the
      // wasCancelled flag already communicate the cancel.
      const reason = (action.payload as string) ?? action.error.message ?? 'Ingest failed';
      if (reason === INGEST_CANCELLED_SENTINEL) {
        state.ingest.wasCancelled = true;
        state.ingest.error = null;
      } else {
        state.ingest.error = reason;
      }
    });

    // ── Cancel ingest ──
    builder.addCase(cancelIngestBatch.pending, (state) => {
      state.ingest.isCancelling = true;
    });
    builder.addCase(cancelIngestBatch.fulfilled, (state) => {
      state.ingest.wasCancelled = true;
      // isCancelling stays true until ingestBatch settles — the user shouldn't
      // see the button re-enable while the http POST is still draining.
    });
    builder.addCase(cancelIngestBatch.rejected, (state) => {
      // Quiet-fail: the upload-abort already did the user-visible work, so
      // don't alert. Just clear the spinner.
      state.ingest.isCancelling = false;
    });

    // ── ASN list ──
    builder.addCase(fetchAsnList.pending, (state) => {
      state.browser.isListLoading = true;
      state.browser.listError = null;
    });
    builder.addCase(fetchAsnList.fulfilled, (state, action) => {
      state.browser.isListLoading = false;
      // Spec pack-analysis-asn-browser-pagination-v2 Req 5.1 / P3 — REPLACE
      // not APPEND. The classic paginator commits the user to N pages of
      // exactly `pageSize` rows; cumulative-append (the prior Load More
      // pattern) bloats memory and breaks the "page N of M" mental model.
      state.browser.list = action.payload.items;
      state.browser.totalCount = action.payload.totalCount;
      state.browser.pageIndex = action.payload.pageIndex;
      state.browser.pageSize = action.payload.pageSize;
    });
    builder.addCase(fetchAsnList.rejected, (state, action) => {
      state.browser.isListLoading = false;
      state.browser.listError = (action.payload as string) ?? action.error.message ?? 'List failed';
    });

    // ── ASN canonical drill-down ──
    builder.addCase(fetchAsnCanonical.pending, (state) => {
      state.browser.isDetailLoading = true;
      state.browser.detailError = null;
    });
    builder.addCase(fetchAsnCanonical.fulfilled, (state, action) => {
      state.browser.isDetailLoading = false;
      state.browser.detailById[action.payload.doc856Id] = action.payload.data;
    });
    builder.addCase(fetchAsnCanonical.rejected, (state, action) => {
      state.browser.isDetailLoading = false;
      state.browser.detailError = (action.payload as string) ?? action.error.message ?? 'Drill-down failed';
    });

    // ── PackMaster list (simulator picker) ──
    builder.addCase(fetchPackMasterList.pending, (state) => {
      state.simulator.isPickerLoading = true;
      state.simulator.pickerError = null;
    });
    builder.addCase(fetchPackMasterList.fulfilled, (state, action) => {
      state.simulator.isPickerLoading = false;
      state.simulator.pickerItems = action.payload.items;
      state.simulator.pickerTotalCount = action.payload.totalCount;
    });
    builder.addCase(fetchPackMasterList.rejected, (state, action) => {
      state.simulator.isPickerLoading = false;
      state.simulator.pickerError = (action.payload as string) ?? action.error.message ?? 'List failed';
    });

    // ── PackMaster get-by-id (simulator seeding) ──
    builder.addCase(fetchPackMasterById.fulfilled, (state, action) => {
      state.simulator.selectedPackMaster = action.payload;
    });

    // ── Cartonization simulate ──
    builder.addCase(cartonizeSimulate.pending, (state) => {
      state.simulator.isSimulating = true;
      state.simulator.error = null;
      // Drop the previous run's plan so a new (and especially a *failed*) run
      // never renders alongside a stale "Plan ready" panel — a `Fail` response
      // (e.g. NOT_YET_IMPLEMENTED, do-NOT-regress #11) lands in `.rejected` and
      // leaves `lastResult` untouched otherwise.
      state.simulator.lastResult = null;
    });
    builder.addCase(cartonizeSimulate.fulfilled, (state, action) => {
      state.simulator.isSimulating = false;
      // Spec pa-shipment-aware-pack-planning — the Simulator has no ShipmentKey input, so it is
      // single-shipment in practice; normalize defensively (display the first shipment if a
      // composite ever arrives) so the union type resolves to a CartonizationResponse.
      state.simulator.lastResult = normalizeCartonizeResult(action.payload).displayPlan;
    });
    builder.addCase(cartonizeSimulate.rejected, (state, action) => {
      state.simulator.isSimulating = false;
      state.simulator.lastResult = null;
      state.simulator.error = (action.payload as string) ?? action.error.message ?? 'Simulate failed';
    });

    // ── Pack Planner cartonize — dual-mode parallel fetch (spec
    //    pack-planner-build-timeline Req 11). PROGRESSIVE drives the Plan
    //    card + §0 + §2; ALTERNATIVES drives §3 only. Independent loading /
    //    error state per request so a failure on one doesn't blank the
    //    other's UI (Req 11.5/11.6/11.7).
    //
    //    Legacy `lastResult` / `isSimulating` / `error` fields are KEPT IN
    //    SYNC with progressive for any external code that still reads them;
    //    new consumers (PackPlannerResultPanel) read the dual-mode fields
    //    directly.

    builder.addCase(cartonizePackPlannerProgressive.pending, (state) => {
      state.packPlanner.isLoadingProgressive = true;
      state.packPlanner.progressiveError = null;
      state.packPlanner.progressiveResult = null;
      // Spec pa-shipment-aware-pack-planning — clear stale multi-shipment state on a new run.
      state.packPlanner.shipments = null;
      state.packPlanner.selectedShipmentKey = null;
      // Keep legacy in sync — `isSimulating` is the "any in flight" flag.
      state.packPlanner.isSimulating = true;
      state.packPlanner.error = null;
      state.packPlanner.lastResult = null;
    });
    builder.addCase(cartonizePackPlannerProgressive.fulfilled, (state, action) => {
      state.packPlanner.isLoadingProgressive = false;
      // Spec pa-shipment-aware-pack-planning — a multi-shipment run returns the composite; store the
      // per-shipment list + display the first shipment. A single-shipment run is unchanged.
      const n = normalizeCartonizeResult(action.payload);
      state.packPlanner.progressiveResult = n.displayPlan;
      state.packPlanner.shipments = n.shipments;
      state.packPlanner.selectedShipmentKey = n.selectedKey;
      // Legacy mirror — progressive is the canonical Plan card source.
      state.packPlanner.lastResult = n.displayPlan;
      // `isSimulating` stays true if alternatives still in flight.
      if (!state.packPlanner.isLoadingAlternatives) {
        state.packPlanner.isSimulating = false;
      }
    });
    builder.addCase(cartonizePackPlannerProgressive.rejected, (state, action) => {
      state.packPlanner.isLoadingProgressive = false;
      state.packPlanner.progressiveResult = null;
      state.packPlanner.progressiveError =
        (action.payload as string) ?? action.error.message ?? 'Progressive request failed';
      // Don't set legacy `error` yet — Req 11.6 says we may fall back to
      // alternatives if it succeeds. The hard-error path is when BOTH fail
      // — selectors derive that combined state.
      if (!state.packPlanner.isLoadingAlternatives) {
        state.packPlanner.isSimulating = false;
      }
    });

    builder.addCase(cartonizePackPlannerAlternatives.pending, (state) => {
      state.packPlanner.isLoadingAlternatives = true;
      state.packPlanner.alternativesError = null;
      state.packPlanner.alternativesResult = null;
      state.packPlanner.alternativesShipments = null;
      state.packPlanner.isSimulating = true;
    });
    builder.addCase(cartonizePackPlannerAlternatives.fulfilled, (state, action) => {
      state.packPlanner.isLoadingAlternatives = false;
      // Spec pa-shipment-aware-pack-planning — mirror the progressive split so the alternatives
      // panel follows the selected shipment.
      const n = normalizeCartonizeResult(action.payload);
      state.packPlanner.alternativesResult = n.displayPlan;
      state.packPlanner.alternativesShipments = n.shipments;
      if (!state.packPlanner.isLoadingProgressive) {
        state.packPlanner.isSimulating = false;
      }
    });
    builder.addCase(cartonizePackPlannerAlternatives.rejected, (state, action) => {
      state.packPlanner.isLoadingAlternatives = false;
      state.packPlanner.alternativesResult = null;
      state.packPlanner.alternativesError =
        (action.payload as string) ?? action.error.message ?? 'Alternatives request failed';
      if (!state.packPlanner.isLoadingProgressive) {
        state.packPlanner.isSimulating = false;
      }
    });

    // ── Item-identifier combobox typeahead (spec …item-aware-line-entry) ──
    builder.addCase(searchItemsForSimulator.pending, (state, action) => {
      state.simulator.itemSearch.loading = true;
      state.simulator.itemSearch.error = null;
      state.simulator.itemSearch.q = action.meta.arg.q;
    });
    builder.addCase(searchItemsForSimulator.fulfilled, (state, action) => {
      // Ignore a stale response whose query no longer matches what the operator typed.
      if (action.meta.arg.q !== state.simulator.itemSearch.q) return;
      state.simulator.itemSearch.loading = false;
      state.simulator.itemSearch.items = action.payload;
    });
    builder.addCase(searchItemsForSimulator.rejected, (state, action) => {
      if (action.meta.arg.q !== state.simulator.itemSearch.q) return;
      state.simulator.itemSearch.loading = false;
      state.simulator.itemSearch.items = [];
      state.simulator.itemSearch.error = (action.payload as string) ?? action.error.message ?? 'Item search failed';
    });

    // ── Pack Planner item-identifier typeahead (parallel to simulator's). ──
    builder.addCase(searchItemsForPackPlanner.pending, (state, action) => {
      state.packPlanner.itemSearch.loading = true;
      state.packPlanner.itemSearch.error = null;
      state.packPlanner.itemSearch.q = action.meta.arg.q;
    });
    builder.addCase(searchItemsForPackPlanner.fulfilled, (state, action) => {
      if (action.meta.arg.q !== state.packPlanner.itemSearch.q) return;
      state.packPlanner.itemSearch.loading = false;
      state.packPlanner.itemSearch.items = action.payload;
    });
    builder.addCase(searchItemsForPackPlanner.rejected, (state, action) => {
      if (action.meta.arg.q !== state.packPlanner.itemSearch.q) return;
      state.packPlanner.itemSearch.loading = false;
      state.packPlanner.itemSearch.items = [];
      state.packPlanner.itemSearch.error = (action.payload as string) ?? action.error.message ?? 'Item search failed';
    });
  },
});

export const {
  setBrowserFilters,
  setBrowserState,
  setBrowserSort,
  clearBrowserDetailCache,
  invalidateAsnDetail,
  setSimulatorSelected,
  clearSimulatorResult,
  setPoHeader,
  setCriteria,
  addPoLine,
  updatePoLine,
  updatePoLineById,
  removePoLine,
  setPoLines,
  addResolvedLine,
  setLineResolution,
  clearLineResolution,
  addPrepackGroup,
  removePrepackGroup,
  setPrepackCount,
  setItemSearchQuery,
  clearItemSearch,
  setItemMasterPickerOpen,
  setPickerOpen,
  // Pack Planner — parallel actions (spec pack-planner Req 1.4)
  clearPackPlannerResult,
  selectPackPlannerShipment,
  setPackPlannerPoHeader,
  setPackPlannerCriteria,
  addPackPlannerPoLine,
  updatePackPlannerPoLine,
  updatePackPlannerPoLineById,
  removePackPlannerPoLine,
  setPackPlannerPoLines,
  addPackPlannerPrepackGroup,
  setPackPlannerItemMasterPickerOpen,
  setPackPlannerPickerOpen,
  addPackPlannerResolvedLine,
  setPackPlannerLineResolution,
  clearPackPlannerLineResolution,
  setPackPlannerItemSearchQuery,
  clearPackPlannerItemSearch,
  setPackPlannerPrepackCount,
  removePackPlannerPrepackGroup,
  resetOnTenantSwitch,
  clearIngestHistory,
  setIngestProgress,
  setIngestCurrentBatchId,
  applyIngestProgressEvent,
  setActiveBatchStarted,
  setActiveBatchCleared,
  acknowledgeBatchCompletion,
  setNotificationPermissionPrompted,
} = packAnalysisWorkbenchSlice.actions;

export default packAnalysisWorkbenchSlice.reducer;
